"use client";

import React, { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import ConfirmationModal from "@/components/Modals/ConfirmationModal";
import { createClient } from "@/utils/supabase/client";
import { generateProductSchemas } from "@/lib/esim-product-schema";
import type { EsimPackage } from "@/lib/esim-go";
import { DevicePhoneMobileIcon, BoltIcon, SignalIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { getEsimPackages } from "@/lib/actions/esim.actions";

type Country = {
  id: number | string;
  country_name: string;
  country_flag?: string;
  country_alpha_code?: string;
};

interface EsimRoamingSectionProps {
  country: Country;
  countryParam: string; // Hyphenated country name for URL
}

export default function EsimRoamingSection({
  country,
  countryParam,
}: EsimRoamingSectionProps) {
  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [sortBy, setSortBy] = useState<"none" | "gb" | "duration">("none");

  const [purchaseResult, setPurchaseResult] = useState<any>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [generatingQr, setGeneratingQr] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "info" as "info" | "warning" | "danger",
    confirmText: "OK",
    cancelText: "Cancel",
    showCancelButton: true,
    onConfirm: () => {},
  });

  const closeModal = () => {
    setModalOpen(false);
  };

  // Fetch packages for the country on mount
  useEffect(() => {
    if (!country?.country_alpha_code) {
      return;
    }

    setLoading(true);
    // Use Server Action instead of public API
    getEsimPackages(country.country_alpha_code)
      .then((data) => {
        if (data.packages && Array.isArray(data.packages)) {
          setPackages(data.packages);
        } else {
          setPackages([]);
        }
        if (data.error) {
          console.error("Error fetching packages:", data.error);
        }
      })
      .catch((err) => {
        console.error("Error fetching packages:", err);
        setPackages([]);
      })
      .finally(() => setLoading(false));
  }, [country]);

  // Sort packages based on selected sort option
  const sortedPackages = useMemo(() => {
    if (sortBy === "none" || packages.length === 0) {
      return packages;
    }

    const sorted = [...packages];

    if (sortBy === "gb") {
      sorted.sort((a, b) => {
        // Handle unlimited packages (dataAmount === -1 or isUnlimited flag) - put them at the end
        const aIsUnlimited = a.isUnlimited || a.dataAmount === -1 || a.dataAmount === null || a.dataAmount === undefined;
        const bIsUnlimited = b.isUnlimited || b.dataAmount === -1 || b.dataAmount === null || b.dataAmount === undefined;

        if (aIsUnlimited && bIsUnlimited) return 0;
        if (aIsUnlimited) return 1; // Unlimited goes to end
        if (bIsUnlimited) return -1; // Unlimited goes to end

        // Convert MB to GB for comparison
        // Handle edge cases where dataAmount might be 0 or invalid
        const aDataAmount = a.dataAmount || 0;
        const bDataAmount = b.dataAmount || 0;
        const aGB = aDataAmount / 1024;
        const bGB = bDataAmount / 1024;
        
        return aGB - bGB; // Sort ascending: 1GB, 5GB, 10GB, Unlimited
      });
    } else if (sortBy === "duration") {
      sorted.sort((a, b) => {
        // Handle edge cases where duration might be null/undefined
        const aDuration = a.duration || 0;
        const bDuration = b.duration || 0;
        return aDuration - bDuration; // Sort ascending: 1 day, 7 days, 30 days, etc.
      });
    }

    return sorted;
  }, [packages, sortBy]);

  const handlePurchase = async (pkg: any) => {
    const supabase = createClient();

    // Check if user is logged in
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      setModalConfig({
        title: "Login Required",
        message: "Please login to purchase eSIM packages.",
        type: "warning",
        confirmText: "Go to Login",
        cancelText: "Cancel",
        showCancelButton: true,
        onConfirm: () => {
          window.location.href = "/auth/login";
        },
      });
      setModalOpen(true);
      return;
    }

    // Check user balance
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("balance")
      .eq("id", session.user.id)
      .single();

    if (userError || !userData) {
      setModalConfig({
        title: "Error",
        message: "Failed to fetch your account balance. Please try again.",
        type: "danger",
        confirmText: "Close",
        cancelText: "",
        showCancelButton: false,
        onConfirm: closeModal,
      });
      setModalOpen(true);
      return;
    }

    const currentBalance = userData.balance || 0;
    const price = pkg.price;

    if (currentBalance < price) {
      setModalConfig({
        title: "Insufficient Balance",
        message: `You need $${price.toFixed(
          2
        )} but your current balance is $${currentBalance.toFixed(
          2
        )}. Please top up your account to continue.`,
        type: "warning",
        confirmText: "Top Up",
        cancelText: "Cancel",
        showCancelButton: true,
        onConfirm: () => {
          // Build referral URL with context for eSIM purchase
          const referralParams = new URLSearchParams({
            ref: 'esim',
            country: countryParam || country.country_alpha_code || '',
            package: pkg.id || '',
            price: pkg.price?.toString() || '',
          });
          window.location.href = `/account/add-credit?${referralParams.toString()}`;
        },
      });
      setModalOpen(true);
      return;
    }

    // If all checks pass, show confirmation modal
    setModalConfig({
      title: "Confirm Purchase",
      message: `Are you sure you want to purchase ${pkg.name} for $${pkg.price.toFixed(2)}?`,
      type: "warning",
      confirmText: "Purchase",
      cancelText: "Cancel",
      showCancelButton: true,
      onConfirm: () => executePurchase(pkg),
    });
    setModalOpen(true);
  };

  const executePurchase = async (pkg: any) => {
    setModalOpen(false); // Close confirmation modal
    setPurchasing(true);
    try {
      const res = await fetch("/api/esim/order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package: pkg }),
      });
      const data = await res.json();
      if (res.ok) {
        setPurchaseResult(data.order);
        setQrCodeUrl(null); // Reset QR code for new purchase
        // Success is handled by UI update, no need for alert/modal
      } else {
        setModalConfig({
          title: "Purchase Failed",
          message: `Purchase failed: ${data.error}`,
          type: "danger",
          confirmText: "Close",
          cancelText: "",
          showCancelButton: false,
          onConfirm: closeModal,
        });
        setModalOpen(true);
      }
    } catch (error) {
      console.error("Purchase error:", error);
      setModalConfig({
        title: "Error",
        message: "An error occurred during purchase.",
        type: "danger",
        confirmText: "Close",
        cancelText: "",
        showCancelButton: false,
        onConfirm: closeModal,
      });
      setModalOpen(true);
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <>
      <div className="pt-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-semibold text-2xl text-gray-800">
              Data Roaming Travel eSIMs for {country.country_name}
            </h2>
            <p className="text-gray-600 mt-1">
              Get instant travel eSIM data roaming plans for {country.country_name}. No physical SIM card needed.
            </p>
          </div>
          <Link
            href={`/global-esim-data-roaming/${countryParam}`}
            className="text-[#090EDB] hover:text-[#E73DC4] font-medium text-sm transition-colors"
          >
            View All Travel eSIM Plans →
          </Link>
        </div>

        {/* Status/Title Section */}
        {purchaseResult ? (
          <div className="bg-white p-6 rounded-xl shadow-lg text-left space-y-4 max-w-2xl">
            <h3 className="text-2xl font-bold text-green-600">
              ✓ Purchase Successful!
            </h3>
            <p className="text-gray-700">
              <strong>Order Reference:</strong> {purchaseResult.orderReference}
            </p>
            <div className="flex flex-col items-center bg-gray-50 p-4 rounded-lg space-y-4">
              {/* QR Code - Only shown if user generates it */}
              {qrCodeUrl && (
                <>
                  <Image
                    src={qrCodeUrl}
                    alt="eSIM QR Code"
                    width={200}
                    height={200}
                    className="rounded"
                  />
                  <p className="text-sm text-gray-500">
                    Scan this QR code to install your eSIM
                  </p>
                </>
              )}
              
              {/* Installation Links - Default method */}
              <div className="w-full space-y-2">
                <p className="text-sm font-semibold text-gray-700 text-center">
                  Click to install directly:
                </p>
                {purchaseResult.iosInstallUrl && (
                  <a
                    href={purchaseResult.iosInstallUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-black text-white text-center py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors font-semibold"
                  >
                    📱 Install on iOS
                  </a>
                )}
                {purchaseResult.androidInstallUrl && (
                  <a
                    href={purchaseResult.androidInstallUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-green-600 text-white text-center py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  >
                    🤖 Install on Android
                  </a>
                )}
              </div>

              {/* Generate QR Code Button */}
              {!qrCodeUrl && (
                <button
                  onClick={async () => {
                    if (!purchaseResult.smdpAddress || !purchaseResult.activationCode) return;
                    
                    setGeneratingQr(true);
                    try {
                      const response = await fetch("/api/esim/qr-code", {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          orderReference: purchaseResult.orderReference,
                          smdpAddress: purchaseResult.smdpAddress,
                          activationCode: purchaseResult.activationCode,
                        }),
                      });

                      if (response.ok) {
                        const data = await response.json();
                        setQrCodeUrl(data.qrCodeUrl);
                      } else {
                        console.error("Failed to generate QR code");
                      }
                    } catch (error) {
                      console.error("Error generating QR code:", error);
                    } finally {
                      setGeneratingQr(false);
                    }
                  }}
                  disabled={generatingQr}
                  className="w-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 transition-colors py-2 px-4 rounded-lg text-sm font-semibold text-gray-700"
                >
                  {generatingQr ? "Generating..." : "📷 Generate QR Code"}
                </button>
              )}
            </div>
            <div className="bg-gray-100 p-4 rounded-lg text-sm space-y-2">
              <p className="break-all">
                <strong>SM-DP+ Address:</strong> {purchaseResult.smdpAddress}
              </p>
              <p className="break-all">
                <strong>Activation Code:</strong>{" "}
                {purchaseResult.activationCode}
              </p>
            </div>
            <button
              onClick={() => {
                setPurchaseResult(null);
                setQrCodeUrl(null); // Reset QR code when buying another package
              }}
              className="w-full bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:opacity-90 transition-opacity py-3 text-white font-semibold rounded-lg"
            >
              Buy Another Package
            </button>
          </div>
        ) : (
          <>
            {!loading && packages.length > 0 ? (
              <>
                {/* Sort Dropdown */}
                <div className="pt-6 pb-4 flex justify-end">
                  <div className="flex items-center gap-3">
                    <label htmlFor="sort-select" className="text-sm font-medium text-gray-700">
                      Sort by:
                    </label>
                    <select
                      id="sort-select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as "none" | "gb" | "duration")}
                      className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:border-[#090EDB] focus:outline-none focus:ring-2 focus:ring-[#090EDB] focus:border-transparent transition-colors cursor-pointer"
                    >
                      <option value="none">Default</option>
                      <option value="gb">Data (GB)</option>
                      <option value="duration">Duration (Days)</option>
                    </select>
                  </div>
                </div>

                {/* Google Product Snippets - JSON-LD structured data */}
                {(() => {
                  const baseUrl =
                    process.env.NEXT_PUBLIC_BASE_URL ||
                    process.env.NEXT_PUBLIC_DOMAIN_NAME ||
                    "https://smss.biz";
                  const productSchemas = generateProductSchemas(
                    sortedPackages as EsimPackage[],
                    baseUrl
                  );
                  return productSchemas.map((schema, index) => (
                    <script
                      key={`product-schema-${sortedPackages[index]?.id || index}`}
                      type="application/ld+json"
                      dangerouslySetInnerHTML={{
                        __html: JSON.stringify(schema),
                      }}
                    />
                  ));
                })()}

                <div className="gap-4 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-stretch">
                  {sortedPackages.slice(0, country.country_alpha_code && country.country_alpha_code.length > 2 ? 50 : 4).map((pkg) => (
                    <div
                      key={pkg.id}
                      className={`flex flex-col h-full rounded-xl p-5 shadow-md hover:shadow-lg transition-all border-2 ${
                        pkg.unlimitedType === "ULP"
                          ? "bg-gradient-to-br from-amber-50 to-white border-amber-200 hover:border-amber-400"
                          : "bg-white border-gray-200 hover:border-[#090EDB]"
                      }`}
                    >
                      <div className="flex-1 flex flex-col space-y-3">
                        {/* Title with fixed height */}
                        <h3 className="text-xl font-bold text-gray-900 line-clamp-3 min-h-[5rem]" title={pkg.name}>
                          {pkg.name}
                        </h3>
                        {/* Description with fixed height */}
                        <p className="text-sm text-gray-600 line-clamp-3 min-h-[2.5rem] flex-grow">
                          {pkg.description}
                        </p>
                        {/* Additional Info: Speed, Network Type, Coverage, Compatibility, Activation */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {/* Compatibility badge - always shown */}
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            <DevicePhoneMobileIcon className="w-3.5 h-3.5" />
                            <span>Compatible with Apple iOS and Android</span>
                          </span>
                          {/* Instant activation badge - always shown */}
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <BoltIcon className="w-3.5 h-3.5" />
                            <span>Instant activation</span>
                          </span>
                          {/* Speed badge */}
                          {pkg.unlimitedType === "ULP" ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold shadow-md">
                              <BoltIcon className="w-3.5 h-3.5" />
                              <span>Fastest Speeds</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <SignalIcon className="w-3.5 h-3.5" />
                              <span>{Array.isArray(pkg.speed) ? pkg.speed.join(" / ") : (pkg.speed || "5G / 4G Speed")}</span>
                            </span>
                          )}
                          {pkg.networkType && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <span>📶</span>
                              <span>{pkg.networkType}</span>
                            </span>
                          )}
                          {pkg.coverage && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              <span>🌍</span>
                              <span>{pkg.coverage}</span>
                            </span>
                          )}
                        </div>
                        {/* Price section - always at same position */}
                        <div className="flex items-baseline space-x-1 mt-auto">
                          <span className="text-3xl font-bold text-[#090EDB]">
                            ${pkg.price.toFixed(2)}
                          </span>
                          <span className="text-sm text-gray-500">USD</span>
                        </div>
                      </div>
                      {/* Button always at bottom */}
                      <button
                        onClick={() => handlePurchase(pkg)}
                        disabled={purchasing}
                        className="mt-4 w-full bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:opacity-90 transition-opacity px-4 py-3 text-sm font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {purchasing ? "Processing..." : "Buy Now"}
                      </button>
                    </div>
                  ))}
                </div>

                {packages.length > (country.country_alpha_code && country.country_alpha_code.length > 2 ? 50 : 4) && (
                  <div className="text-center mt-6">
                    <Link
                      href={`/global-esim-data-roaming/${countryParam}`}
                      className="inline-block px-6 py-3 bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:opacity-90 transition-opacity text-white font-semibold rounded-lg"
                    >
                      View All {packages.length} Packages →
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-600">
                <p>No eSIM packages available for {country.country_name} at the moment.</p>
                <Link
                  href={`/global-esim-data-roaming/${countryParam}`}
                  className="mt-4 inline-block text-[#090EDB] hover:text-[#E73DC4] font-medium transition-colors"
                >
                  Browse all available plans →
                </Link>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={modalOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        type={modalConfig.type}
        showCancelButton={modalConfig.showCancelButton}
      />
    </>
  );
}
